import {generateRouteTo} from 'epic-fortnite-react';
import {Inline, Stack, Text, useBreakpoints} from 'epic-fortnite-react';
import {localizedMessage} from 'epic-fortnite-react/components/message/Message';
import {FontSizes} from 'epic-fortnite-react/types/theme';
import * as React from 'react';
import {Link, useLocation} from 'react-router-dom';
import styled from 'styled-components';

import Background from '../../common/Background';
import CTAButton from '../../common/CTAButton';

import {Slide} from './Types';

interface Props {
  slide: Slide;
  mobile: boolean;
  tablet: boolean;
  isAndroid: boolean;
  showVideo: (videoId: string) => void;
}

/**
 * @todo(daniel.wasilewski) 'topCenter' is a current CMS option, repurposed to vertically center the content.
 * It'd be worth exploring making these positions more verbose for additional variants.
 */
interface SlidePosition {
  position: 'center' | 'left' | 'right' | 'topCenter';
}

const StyledCarouselSlide = styled.div`
  height: 0;
  position: relative;
  font-size: 16px;
  padding-top: min(calc(100% * 5 / 3), 90vh);

  @media (min-width: 900px) {
    padding-top: min(calc(100% * 9 / 16), 90vh);
  }
`;

const StyledCarouselContent = styled.div<SlidePosition>`
  align-items: ${(props) =>
    props.position === 'left' || props.position === 'right' ? 'center' : 'start'};
  display: flex;
  height: 90%;
  justify-items: center;
  margin: auto;
  height: 90vh;
  max-height: calc(100% - 110px);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;

  @media (min-width: 900px) {
    display: grid;
    grid-auto-columns: 1fr auto;
    grid-auto-rows: 1fr auto auto;
    height: 90%;
    justify-items: ${(props) =>
      props.position === 'left' || props.position === 'right' ? 'start' : 'center'};
  }

  .season-bug {
    left: 16px;
    top: 16px;
    width: 48px;
    height: 48px;
    position: absolute;

    @media (min-width: 900px) {
      left: 32px;
      top: 32px;
    }
  }

  img {
    max-width: 100%;
  }

  .play-wrapper {
    align-items: center;
    display: flex;
    font-size: 12px;
    justify-self: center;
    overflow: hidden;

    @media (max-width: 999px) {
      margin-bottom: -1em;
    }
  }
`;

const StyledCarouselButton = styled.div`
  display: block;
`;

const StyledSeasonLogo = styled.img<SlidePosition>`
  height: auto;
  width: 100%;

  html[lang='pt-BR'],
  html[lang='en-US'],
  & {
    margin-bottom: 1rem;
  }

  @media (min-width: 900px) {
    max-width: ${(props) => (props.position === 'topCenter' ? '600px' : '350px')} !important;
  }

  @media (min-width: 1200px) {
    max-width: ${(props) => (props.position === 'topCenter' ? '900px' : '500px')} !important;
  }

  @media (min-width: 1600px) {
    max-width: ${(props) => (props.position === 'topCenter' ? '1024px' : '600px')} !important;
  }
`;

const StyledVideoPlayButton = styled.button`
  appearance: none;
  background-color: transparent;
  border: 0;
`;

const FloatingContent = styled.div<SlidePosition>`
  align-self: ${(props) => (props.position === 'center' ? 'stretch' : 'end')};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: ${(props) =>
    props.position === 'center'
      ? 'flex-end'
      : props.position === 'topCenter'
      ? 'center'
      : 'flex-end'};
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 48px;
  position: absolute;
  width: 100%;

  @media (min-width: 500px) {
    padding: 48px;
    padding-bottom: 0;
  }

  @media (max-width: 899px) {
    flex: 1;
  }

  @media (min-width: 900px) {
    align-self: ${(props) => (props.position === 'center' ? 'end' : 'center')};
    justify-content: ${(props) => (props.position === 'center' ? 'flex-end' : 'center')};
    width: ${(props) => props.position !== 'center' && props.position !== 'topCenter' && '50%'};
  }
`;

const Carousel = (props: Props) => {
  const location = useLocation();
  const {slide, mobile, tablet, showVideo, isAndroid} = props;

  const {
    background,
    captionTitle,
    captionDesc,
    captionImage,
    captionDesc2,
    button,
    videoId,
    androidDownload,
    textInsteadOfImage,
    seasonBug,
    desc2Color,
  } = slide;

  const slidePosition =
    slide.captionPos === 'left'
      ? 'left'
      : slide.captionPos === 'right'
      ? 'right'
      : slide.captionPos === 'topCenter'
      ? 'topCenter'
      : 'center';

  const showAndroidDownloadBtn = isAndroid && androidDownload;

  const headingTextSize: FontSizes = useBreakpoints('2xl', {500: '3xl', 900: '4xl'}).value;
  const subheadingTextSize: FontSizes = useBreakpoints('med', {900: 'lg'}).value;

  return (
    <StyledCarouselSlide>
      <Background breakPoint={899} mobile={mobile} tablet={tablet} background={background} />
      <StyledCarouselContent position={slidePosition}>
        {seasonBug && <img alt="Season Bug" className="season-bug" src={seasonBug} />}
        <FloatingContent position={slidePosition}>
          <Stack align="center" justify="center" gap="med">
            {captionImage && !textInsteadOfImage ? (
              <StyledSeasonLogo position={slidePosition} src={captionImage} alt={captionDesc} />
            ) : textInsteadOfImage ? (
              <h1>{captionDesc}</h1>
            ) : null}
            <Stack align="center" justify="center" gap="med">
              {!captionImage && captionTitle && (
                <Text
                  align="center"
                  as="h3"
                  color="#fff"
                  font="OpenSans"
                  size={captionImage ? subheadingTextSize : headingTextSize}
                  transform="uppercase"
                  weight="600">
                  {captionTitle}
                </Text>
              )}

              {captionDesc2 && (
                <Text as="h3" italic={true} size="2xl" transform="uppercase" color={desc2Color}>
                  {captionDesc2}
                </Text>
              )}
              <Inline gapX="sm" gapY="xs" justify="center">
                {videoId && (
                  <StyledVideoPlayButton
                    className="play-wrapper"
                    onClick={() => showVideo(videoId)}>
                    <Inline flex="start" gap="2xs" wrap={false}>
                      <Text color="#fff" size="lg" transform="uppercase">
                        {localizedMessage('epic.fortnite.trailer')}
                      </Text>
                      <svg width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle
                          cx="24"
                          cy="24"
                          r="23.5"
                          fill="#000"
                          fillOpacity=".6"
                          stroke="#fff"
                        />
                        <path
                          d="M31.869 22.4a2 2 0 010 3.2L22.2 32.856c-1.319.99-3.201.049-3.201-1.6V16.744c0-1.648 1.882-2.589 3.2-1.6L31.87 22.4z"
                          fill="#fff"
                        />
                      </svg>
                    </Inline>
                  </StyledVideoPlayButton>
                )}
                <StyledCarouselButton>
                  {showAndroidDownloadBtn ? (
                    <Link
                      to={generateRouteTo('/mobile/android', location)}
                      className="btn btn-primary btn-hover-transform">
                      {localizedMessage('epic.fortnite.battleRoyale.playFreeNow')}
                    </Link>
                  ) : (
                    <CTAButton ctaConfig={button} theme={slide.button.buttonTheme} />
                  )}
                </StyledCarouselButton>
              </Inline>
            </Stack>
          </Stack>
        </FloatingContent>
      </StyledCarouselContent>
    </StyledCarouselSlide>
  );
};

export default Carousel;
